import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Collapse,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Shipment } from '../../models/Shipment';

interface ShipmentsPopupProps {
  open: boolean;
  onClose: () => void;
  shipments: Shipment[];
  orderId: string;
}

interface ShipmentRowProps {
  shipment: Shipment;
  index: number;
}

const ShipmentRow: React.FC<ShipmentRowProps> = ({ shipment, index }) => {
  const { t, i18n } = useTranslation();
  const [isExpanded, setIsExpanded] = React.useState(false);
  
  // Set direction to rtl if the language is Hebrew
  const contentDirection = i18n.language === 'he' ? 'rtl' : 'ltr';

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language === 'he' ? 'he-IL' : 'en-GB');
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString(i18n.language === 'he' ? 'he-IL' : 'en-GB');
  };

  const getShippingTypeColor = (type: string) => {
    switch (type) {
      case 'external':
        return 'primary';
      case 'internalSilo':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{index + 1}</TableCell>
        <TableCell>{shipment.shippingCertificateNumber}</TableCell>
        <TableCell>
          <Chip 
            label={shipment.shippingType} 
            color={getShippingTypeColor(shipment.shippingType) as any}
            size="small"
          />
        </TableCell>
        <TableCell>{shipment.clientName}</TableCell>
        <TableCell>{shipment.truckNum}</TableCell>
        <TableCell>{shipment.driverId}</TableCell>
        <TableCell>{formatDate(shipment.date)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                {t('Shipments.Details')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('Shipments.BasicInfo')}
                  </Typography>
                  <Typography variant="body2" dir={contentDirection}>ID: {shipment._id}</Typography>
                  <Typography variant="body2" dir={contentDirection}>{t('Shipments.SiteOfOrigin')}: {shipment.siteOfOrigin}</Typography>
                  <Typography variant="body2" dir={contentDirection}>{t('Shipments.Destination')}: {shipment.destination || 'N/A'}</Typography>
                  <Typography variant="body2" dir={contentDirection}>{t('Shipments.LoadingTime')}: {shipment.loadingTime || 'N/A'}</Typography>
                  <Typography variant="body2" dir={contentDirection}>{t('Shipments.TransportationCompany')}: {shipment.transportationCompany || 'N/A'}</Typography>
                  <Typography variant="body2" dir={contentDirection}>{t('Shipments.DriverName')}: {shipment.driverName || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('Shipments.ProductInfo')}
                  </Typography>
                  <Typography variant="body2" dir={contentDirection}>{t('Shipments.ProductId')}: {shipment.productId}</Typography>
                  <Typography variant="body2" dir={contentDirection}>{t('Shipments.Manufacturer')}: {shipment.manufacturer}</Typography>
                  <Typography variant="body2" dir={contentDirection}>{t('Shipments.ItemDescription')}: {shipment.itemDescription}</Typography>
                  <Typography variant="body2" dir={contentDirection}>{t('Shipments.CartNote')}: {shipment.cartNote || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('Shipments.WeightInfo')}
                  </Typography>
                  <Typography variant="body2" dir={contentDirection}>{t('Shipments.EntryWeight')}: {shipment.entryWeight || 'N/A'}</Typography>
                  <Typography variant="body2" dir={contentDirection}>{t('Shipments.ExitWeight')}: {shipment.exitWeight || 'N/A'}</Typography>
                  <Typography variant="body2" dir={contentDirection}>{t('Shipments.ProductWeight')}: {shipment.productWeight || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('Shipments.AdditionalInfo')}
                  </Typography>
                  <Typography variant="body2" dir={contentDirection}>{t('Shipments.VesselName')}: {shipment.vesselName || 'N/A'}</Typography>
                  <Typography variant="body2" dir={contentDirection}>{t('Shipments.VoyageNum')}: {shipment.voyageNum || 'N/A'}</Typography>
                  <Typography variant="body2" dir={contentDirection}>{t('Shipments.DestinationReceiverName')}: {shipment.destinationReceiverName || 'N/A'}</Typography>
                  <Typography variant="body2" dir={contentDirection}>{t('Shipments.Comments')}: {shipment.comments || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('Shipments.SystemInfo')}
                  </Typography>
                  <Typography variant="body2" dir={contentDirection}>{t('Shipments.CreatedAt')}: {formatDateTime(shipment.createdAt)}</Typography>
                  <Typography variant="body2" dir={contentDirection}>{t('Shipments.CreatedBy')}: {shipment.createdBy}</Typography>
                  <Typography variant="body2" dir={contentDirection}>{t('Shipments.UserEmail')}: {shipment.userEmail}</Typography>
                  {shipment.priorityErrorMessage && (
                    <Typography variant="body2" color="error" dir={contentDirection}>
                      {t('Shipments.PriorityError')}: {shipment.priorityErrorMessage}
                    </Typography>
                  )}
                </Box>
                {shipment.taskYamIntegration && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('Shipments.TaskYamIntegration')}
                    </Typography>
                    <Typography variant="body2" dir={contentDirection}>
                      {t('Shipments.Success')}: {shipment.taskYamIntegration.success ? t('General.Yes') : t('General.No')}
                    </Typography>
                    {shipment.taskYamIntegration.error && (
                      <Typography variant="body2" color="error" dir={contentDirection}>
                        {t('Shipments.Error')}: {shipment.taskYamIntegration.error.message}
                      </Typography>
                    )}
                    <Typography variant="body2" dir={contentDirection}>
                      {t('Shipments.Skipped')}: {shipment.taskYamIntegration.skipped ? t('General.Yes') : t('General.No')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const ShipmentsPopup: React.FC<ShipmentsPopupProps> = ({ open, onClose, shipments, orderId }) => {
  const { t } = useTranslation();

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xl" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        {t('Shipments.Title')} - {t('Order.OrderId')}: {orderId}
        <Typography variant="subtitle2" color="text.secondary">
          {t('Shipments.Count')}: {shipments.length}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={50}></TableCell>
                <TableCell>{t('Shipments.Number')}</TableCell>
                <TableCell>{t('Shipments.CertificateNumber')}</TableCell>
                <TableCell>{t('Shipments.Type')}</TableCell>
                <TableCell>{t('Shipments.ClientName')}</TableCell>
                <TableCell>{t('Shipments.TruckNumber')}</TableCell>
                <TableCell>{t('Shipments.DriverId')}</TableCell>
                <TableCell>{t('Shipments.Date')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shipments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No shipments found for this order
                  </TableCell>
                </TableRow>
              ) : (
                shipments.map((shipment, index) => (
                  <ShipmentRow key={shipment._id} shipment={shipment} index={index} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          {t('General.Labels.Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShipmentsPopup;
